import { DestroyRef, inject, Injectable } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { NavigationError, NavigationStart, Router } from "@angular/router";
import { SwUpdate, VersionReadyEvent } from "@angular/service-worker";
import { timer } from "rxjs";
import { filter } from "rxjs/operators";
import { clearCacheBustParam, isStaleBuildError, recoverFromStaleBuild, reloadForNewBuild } from "./stale-build-recovery";

/** How often to ask the server which build it is serving now. */
const VERSION_POLL_INTERVAL_MS = 5 * 60_000;

/**
 * Keeps a long-running tab on the build the server actually has.
 *
 * A release renames every lazy chunk and the deploy deletes the previous build, so an open tab
 * breaks the moment it tries to load a chunk it has not needed yet. Two halves:
 *
 * - Detect a new release up front (service worker event, or polling the deploy-stamped version in
 *   server_variables.json) and pick it up at the next route change, which is the one moment a full
 *   page load costs the user nothing.
 * - Recover if a chunk fails anyway, so nobody is left on a half-broken view.
 */
@Injectable({ providedIn: "root" })
export class AppUpdateService {
  private destroyRef = inject(DestroyRef);
  private router = inject(Router);
  private swUpdate = inject(SwUpdate);

  /** Set once we know the server is serving a different build than this tab booted with. */
  private newVersionPending = false;

  /** Guards against kicking off several page loads from one burst of router events. */
  private reloading = false;

  public start(): void {
    clearCacheBustParam();
    this.recoverOnFailedNavigation();
    this.recoverOnUnhandledRejection();
    this.loadNewBuildAtNextNavigation();
    this.watchServiceWorkerVersions();
    this.pollDeployedVersion();
  }

  /** A lazy route whose chunk is gone: the user is stuck on the current view, so reload. */
  private recoverOnFailedNavigation(): void {
    this.router.events
      .pipe(
        filter((event): event is NavigationError => event instanceof NavigationError),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((event) => {
        if (isStaleBuildError(event.error)) {
          recoverFromStaleBuild(`Route ${event.url} could not load its code`);
        }
      });
  }

  /** Dynamic imports outside the router (Highcharts modules, for example) reject unhandled. */
  private recoverOnUnhandledRejection(): void {
    const onRejection = (event: PromiseRejectionEvent): void => {
      if (isStaleBuildError(event.reason)) {
        recoverFromStaleBuild("A lazily imported module could not be loaded");
      }
    };

    window.addEventListener("unhandledrejection", onRejection);
    this.destroyRef.onDestroy(() => window.removeEventListener("unhandledrejection", onRejection));
  }

  private loadNewBuildAtNextNavigation(): void {
    this.router.events
      .pipe(
        filter((event): event is NavigationStart => event instanceof NavigationStart),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((event) => {
        if (!this.newVersionPending || this.reloading) {
          return;
        }

        this.reloading = true;

        // The app is configured with HashLocationStrategy, so the target route is the hash and
        // assigning it does not reload on its own.
        window.location.hash = event.url;

        // Not window.location.reload(): this can fire while the deploy that produced the new build
        // is still extracting, and a reload into that window lands on the IIS 403 page with no
        // script of ours left to recover. reloadForNewBuild waits for the server first.
        reloadForNewBuild(`Loading the new build to open ${event.url}`);
      });
  }

  private watchServiceWorkerVersions(): void {
    if (!this.swUpdate.isEnabled) {
      return;
    }

    this.swUpdate.versionUpdates
      .pipe(
        filter((event): event is VersionReadyEvent => event.type === "VERSION_READY"),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((event) => {
        // Deliberately not activateUpdate(): that swaps the cache under the running app and
        // breaks its next lazy import. Wait for a navigation instead.
        console.warn(`[Update] Service worker fetched a new build (${event.latestVersion.hash}).`);
        this.newVersionPending = true;
      });

    this.swUpdate.unrecoverable.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((event) => {
      recoverFromStaleBuild(`Service worker is in an unrecoverable state (${event.reason})`);
    });
  }

  /**
   * The service worker only ships in the Trips build, so poll the version the deploy stamped into
   * server_variables.json to cover every other site as well.
   */
  private pollDeployedVersion(): void {
    const bootedVersion = this.bootedVersion();
    if (!bootedVersion) {
      // Local builds and unstamped artifacts have no version to compare against.
      return;
    }

    timer(VERSION_POLL_INTERVAL_MS, VERSION_POLL_INTERVAL_MS)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        if (this.newVersionPending || document.visibilityState === "hidden") {
          return;
        }
        void this.checkDeployedVersion(bootedVersion);
      });
  }

  private async checkDeployedVersion(bootedVersion: string): Promise<void> {
    try {
      const response = await fetch("./server_variables.json", { cache: "no-store" });
      if (!response.ok) {
        return;
      }

      const deployed = (await response.json()) as { Version?: string };
      if (deployed.Version && deployed.Version !== bootedVersion) {
        console.warn(`[Update] Server is serving build ${deployed.Version}; this tab booted ${bootedVersion}.`);
        this.newVersionPending = true;
      }
    } catch {
      // Offline or a transient failure; the next tick tries again.
    }
  }

  private bootedVersion(): string {
    return (globalThis as { server_variables?: { Version?: string } }).server_variables?.Version ?? "";
  }
}
